import { Add, Delete } from '@tunasong/icons'
import type { GearCategory, GearRequirement, ProfileGear } from '@tunasong/schemas'
import { gearCategoryValues } from '@tunasong/schemas'
import { Button, DebouncedTextField, IconButton, Select, Typography } from '@tunasong/ui-lib'
import type { FC, KeyboardEvent } from 'react'
import { useId, useState } from 'react'
import { ChoiceGroup } from './choice-group.js'
import type { RowRemoval } from './row-types.js'
import { whoOptions } from './row-types.js'
import { addRequirement, removeRequirement, updateRequirement } from './setup-edits.js'
import type { FocusRequest } from './use-focus-request.js'
import styles from './rows.module.css'

export interface GearRowsProps {
  requirements: GearRequirement[]
  focus: FocusRequest
  update(edit: (current: ProfileGear | undefined) => ProfileGear): void
  onRemoved(removal: RowRemoval): void
}

const categoryLabels: Record<GearCategory, string> = {
  microphone: 'Microphone',
  monitor: 'Monitor',
  stand: 'Stand',
  instrument: 'Instrument',
  backline: 'Backline',
  accessory: 'Accessory',
  other: 'Other',
}

/** Microphones belong to inputs now; only a row that already is one keeps the option, so it can be read and moved */
const categoriesFor = (current: GearCategory) =>
  gearCategoryValues.filter(category => category !== 'microphone' || current === 'microphone')

const emptyItem: GearRequirement = { category: 'backline', name: '', quantity: 1, providedByArtist: false }
const addKey = 'add-gear'
const rowKey = (index: number) => `gear:${index}`

const summary = (item: GearRequirement) =>
  [categoryLabels[item.category], item.providedByArtist ? 'I bring it' : 'Venue provides'].join(' · ')

/** Gear: what needs to be on stage that is not a channel. Rows are addressed by position; there are no ids. */
export const GearRows: FC<GearRowsProps> = props => {
  const { requirements, update, onRemoved, focus } = props
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const headingId = useId()

  const close = (index: number) => {
    setEditingIndex(null)
    if (!requirements[index]?.name.trim()) {
      update(current => removeRequirement(current, index).gear)
      focus.request(addKey)
      return
    }
    focus.request(rowKey(index))
  }

  const handleAdd = () => {
    update(current => addRequirement(current, emptyItem))
    setEditingIndex(requirements.length)
  }

  const handleDelete = (index: number) => {
    const item = requirements[index]
    const hasNeighbour = requirements.length > 1
    let undo: ((current: ProfileGear | undefined) => ProfileGear) | undefined
    update(current => {
      const removal = removeRequirement(current, index)
      undo = removal.undo
      return removal.gear
    })
    setEditingIndex(null)
    // The next row slides into this position; the last row's neighbour is the one before it
    focus.request(hasNeighbour ? rowKey(index < requirements.length - 1 ? index : index - 1) : addKey)
    onRemoved({
      message: `Removed ${item?.name.trim() || 'item'}.`,
      onUndo: () => {
        if (undo) {
          const restore = undo
          update(current => restore(current))
        }
        focus.request(rowKey(index))
      },
    })
  }

  const handleKeyDown = (index: number) => (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      close(index)
    }
  }

  const patch = (index: number, change: Partial<GearRequirement>) =>
    update(current => updateRequirement(current, index, change))

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <Typography id={headingId} variant="h6" component="h3" className={styles.title}>
        Gear
      </Typography>
      <Typography variant="body2" color="textSecondary" className={styles.intro}>
        What needs to be on stage: backline, stands, power.
      </Typography>
      <ul className={styles.list}>
        {requirements.map((item, index) =>
          index === editingIndex ? (
            <li key={index} className={styles.editing}>
              <div
                role="group"
                aria-label={`Gear: ${item.name.trim() || 'new'}`}
                className={styles.form}
                onKeyDown={handleKeyDown(index)}
              >
                <Select
                  label="Category"
                  value={item.category}
                  onChange={event => patch(index, { category: event.target.value as GearCategory })}
                >
                  {categoriesFor(item.category).map(category => (
                    <option key={category} value={category}>
                      {categoryLabels[category]}
                    </option>
                  ))}
                </Select>
                <DebouncedTextField
                  label="Item"
                  value={item.name}
                  autoFocus
                  onDebouncedChange={name => patch(index, { name })}
                />
                <DebouncedTextField
                  label="Quantity"
                  type="number"
                  min={1}
                  value={String(item.quantity)}
                  onDebouncedChange={value => {
                    const quantity = Number.parseInt(value, 10)
                    patch(index, { quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1 })
                  }}
                />
                <ChoiceGroup
                  legend="Who brings it"
                  options={whoOptions}
                  value={item.providedByArtist ? 'artist' : 'venue'}
                  onChange={who => patch(index, { providedByArtist: who === 'artist' })}
                />
                <DebouncedTextField
                  label="Notes"
                  value={item.notes ?? ''}
                  onDebouncedChange={notes => patch(index, { notes: notes.trim() || undefined })}
                />
                <div className={styles.formActions}>
                  <Button variant="outlined" color="inherit" onClick={() => handleDelete(index)}>
                    Delete item
                  </Button>
                  <Button variant="contained" onClick={() => close(index)}>
                    Done
                  </Button>
                </div>
              </div>
            </li>
          ) : (
            <li key={index} className={styles.row}>
              <button
                type="button"
                className={styles.summary}
                aria-expanded={false}
                ref={focus.register(rowKey(index))}
                onClick={() => setEditingIndex(index)}
              >
                <span className={styles.primary}>
                  {item.quantity > 1 ? `${item.quantity}× ` : ''}
                  {item.name.trim() || 'Untitled item'}
                </span>
                <span className={styles.secondary}>{summary(item)}</span>
              </button>
              <IconButton
                aria-label={`Delete ${item.name.trim() || 'item'}`}
                size="small"
                onClick={() => handleDelete(index)}
              >
                <Delete size={16} />
              </IconButton>
            </li>
          )
        )}
      </ul>
      <Button startIcon={<Add />} ref={focus.register(addKey)} onClick={handleAdd}>
        Add gear
      </Button>
    </section>
  )
}

export default GearRows
